import { FC } from 'react'

import { FileUpload } from 'components/Forms'
import { useUpload, useGetUrl } from 'components/Hooks'
import { Button, List, Card } from 'ui'
import { UPLOAD_ATTACHMENTS_TYPES } from 'common'
import { GetUrlHandler, UploadHandler } from 'components/Hooks/attachments'
import { UserFile } from 'Api'
import { createNotification } from 'store/actions/notifications'

import { t } from 'translations'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'

type Props = {
  uploadHandler: UploadHandler
  getUrlHandler: GetUrlHandler
  onAfterDrop: (file?: UserFile) => void
  onError?: () => void
  onSuccess?: () => void
  onAfterRemove: (file?: UserFile) => void
  files: UserFile[]
}

export const FileAttachments: FC<Props> = ({ onAfterDrop, onAfterRemove, files, uploadHandler, getUrlHandler, onError, onSuccess }) => {
  const handleFileDrop = async ({ file, onAfterDrop }) => {
    const key = await uploadHandler({ fileName: file.name, file, contentType: file.type })
    if (!key) return onError?.()

    const newFile: UserFile = { key, name: file.name, type: file.type }
    onAfterDrop(newFile)
    onSuccess?.()
  }

  const handleRemove = async ({ file, onAfterRemove }) => {
    if (!file.key) return
    onAfterRemove(file)
  }

  return (
    <Card className="mb-3">
      <Card.Header>
        <Card.Title className="my-0">{t('forms.fileUpload.ATTACHMENTS')}</Card.Title>
      </Card.Header>
      <Card.Body>
        <FileUpload onChange={file => handleFileDrop({ file, onAfterDrop })} />
        <List>
          {files?.map(file => {
            if (!file) return null
            return (
              <List.Item className="list-group-item col-12 d-flex flex-row" key={file.name}>
                {file.type === 'application/pdf' ? (
                  <span className="mr-2">
                    <i className="far fa-file-pdf fa-2x" />
                  </span>
                ) : (
                  <span className="mr-2">
                    <i className="far fa-image fa-2x" />
                  </span>
                )}
                <div className="flex-fill">
                  <div>
                    <h6 className="d-inline">
                      <strong data-testid="file-name">{file.name}</strong>
                    </h6>
                    <>
                      <strong> · </strong>
                      <Button isLink color="error" onClick={() => handleRemove({ onAfterRemove, file })} type="button" data-testid="remove">
                        {t('forms.REMOVE')}
                      </Button>
                    </>
                  </div>
                  <div>
                    <Button
                      isLink
                      color="info"
                      onClick={async () => {
                        const url = await getUrlHandler({ key: file.key })
                        if (url) window.open(url, '_blank')
                      }}
                    >
                      {t('forms.fileUpload.OPEN_FILE')}
                    </Button>
                  </div>
                </div>
              </List.Item>
            )
          })}
        </List>
      </Card.Body>
    </Card>
  )
}

type FileAttachmentsContainerProps = Omit<Props, 'uploadHandler' | 'getUrlHandler'> & {
  attachmentType: ValueOf<typeof UPLOAD_ATTACHMENTS_TYPES>
}

const FileAttachmentsContainer: FC<FileAttachmentsContainerProps> = ({ files, onAfterDrop, onAfterRemove, attachmentType }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { uploadHandler } = useUpload({ attachmentType })
  const { getUrlHandler } = useGetUrl({ attachmentType })

  const onError = () => {
    dispatch(createNotification({ title: t('upload.UPLOAD_FAILED.TITLE'), description: t('UPLOAD_FAILED.DESCRIPTION'), type: 'danger' }))
  }

  const onSuccess = () => {
    dispatch(createNotification({ title: t('upload.UPLOAD_SUCCESS.TITLE'), description: t('upload.UPLOAD_SUCCESS.DESCRIPTION'), type: 'success' }))
  }

  return (
    <FileAttachments
      onError={onError}
      onSuccess={onSuccess}
      uploadHandler={uploadHandler}
      files={files}
      onAfterDrop={onAfterDrop}
      onAfterRemove={onAfterRemove}
      getUrlHandler={getUrlHandler}
    />
  )
}

export default FileAttachmentsContainer
